import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  jsonLd?: object;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  jsonLd,
}: SEOProps) {
  useEffect(() => {
    // Title
    document.title = title;

    // Meta tags
    const metaTags: { [key: string]: string } = {
      description,
      keywords: keywords || '',
      'og:title': title,
      'og:description': description,
      'og:type': ogType,
      'twitter:card': 'summary_large_image',
      'twitter:title': title,
      'twitter:description': description,
    };

    if (ogImage) {
      metaTags['og:image'] = ogImage;
      metaTags['twitter:image'] = ogImage;
    }

    if (canonical) {
      metaTags['og:url'] = canonical;
    }

    // Update or create meta tags
    Object.entries(metaTags).forEach(([name, content]) => {
      if (!content) return;

      const isOg = name.startsWith('og:');
      const isTwitter = name.startsWith('twitter:');
      const attr = isOg || isTwitter ? 'property' : 'name';
      
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    });

    // Canonical link
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // Last-modified
    const lastModified = new Date().toISOString();
    let lastModMeta = document.querySelector('meta[name="last-modified"]');
    if (!lastModMeta) {
      lastModMeta = document.createElement('meta');
      lastModMeta.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModMeta);
    }
    lastModMeta.setAttribute('content', lastModified);

    // JSON-LD
    if (jsonLd) {
      const scriptId = 'jsonld-schema';
      let script = document.getElementById(scriptId) as HTMLScriptElement;
      
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      
      script.textContent = JSON.stringify(jsonLd);
    }

    // Cleanup function
    return () => {
      const scriptId = 'jsonld-schema';
      const script = document.getElementById(scriptId);
      if (script) {
        script.remove();
      }
    };
  }, [title, description, keywords, canonical, ogType, ogImage, jsonLd]);
}

export function generateWebPageSchema(
  title: string,
  description: string,
  url: string
) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: `${siteUrl}${url}`,
    publisher: {
      '@type': 'Organization',
      name: 'キョウコレ',
      url: siteUrl,
    },
  };
}

export function generateArticleSchema(
  title: string,
  description: string,
  url: string,
  datePublished: string,
  dateModified?: string
) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    url: `${siteUrl}${url}`,
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      '@type': 'Organization',
      name: 'キョウコレ',
    },
    publisher: {
      '@type': 'Organization',
      name: 'キョウコレ',
      url: siteUrl,
    },
  };
}

export function generateBreadcrumbSchema(items: { name: string; url: string }[]) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`,
    })),
  };
}

export function generateFAQSchema(faqs: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export const updatePageMeta = (config: PageMetaConfig) => {
  // Title
  document.title = config.title;

  // Description
  updateMetaTag('name', 'description', config.description);

  // Keywords
  if (config.keywords) {
    updateMetaTag('name', 'keywords', config.keywords);
  }

  // Canonical URL - www付きに統一
  const canonicalUrl = config.canonicalUrl || `https://www.kyo-colle.com${window.location.pathname}`;
  updateLinkTag('canonical', canonicalUrl);

  // OG Tags - www付きに統一
  const ogUrl = config.ogUrl || `https://www.kyo-colle.com${window.location.pathname}`;
  updateMetaTag('property', 'og:title', config.ogTitle || config.title);
  updateMetaTag('property', 'og:description', config.ogDescription || config.description);
  updateMetaTag('property', 'og:url', ogUrl);
  
  if (config.ogImage) {
    updateMetaTag('property', 'og:image', config.ogImage);
    updateMetaTag('property', 'og:image:secure_url', config.ogImage);
  }

  // Twitter Card - www付きに統一
  updateMetaTag('property', 'twitter:title', config.twitterTitle || config.title);
  updateMetaTag('property', 'twitter:description', config.twitterDescription || config.description);
  updateMetaTag('property', 'twitter:url', ogUrl);
  
  if (config.twitterImage || config.ogImage) {
    updateMetaTag('property', 'twitter:image', config.twitterImage || config.ogImage || '');
  }

  // Last Modified
  updateMetaTag('name', 'last-modified', new Date().toISOString().split('T')[0]);
};
